import { useState, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Eye } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formattedName } from "../../../utils/utils";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { updateDashboardData } from "../../../redux/slice/brainSlice";
import { useDispatch } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import {
  getBrainDashboard,
  getSubmissionDashboardData,
  getUserReAssignList,
  ReAssignUser,
} from "../../../services/api";
import useApi from "../../../hooks/useApi";
import { brainStyles } from "../../../theme/style";
import APP_CONSTANTS from "../../../utils/config";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import StatusChip from "../components/StatusChip";
import { Edit } from "@mui/icons-material";
import Error from "../../../components/Error/Error";
import { updateAlertInfo } from "../../../redux/slice/userSlice";

function ViewToggle({ theme, value, onChange }) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v) => v && onChange(v)}
      sx={{
        "& .MuiToggleButton-root": {
          px: 1,
          py: 0.35,
          textTransform: "none",
          fontSize: "0.72rem",
          fontWeight: 500,
          borderColor: theme.palette.secondary.main2,
          color: theme.palette.brain.table.row.text,
        },
        "& .Mui-selected": {
          backgroundColor: `${theme.palette.secondary.main2} !important`,
          color: "#fff !important",
          fontWeight: 600,
        },

        "& .Mui-selected:hover": {
          bgcolor: theme.palette.secondary.main2,
        },
      }}
    >
      <ToggleButton value="graph">
        <GridViewIcon sx={{ fontSize: 15, mr: 0.5 }} />
        Graph
      </ToggleButton>
      <ToggleButton value="table">
        <TableRowsIcon sx={{ fontSize: 15, mr: 0.5 }} />
        Table
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

function StatusDot({ status, statusMeta }) {
  const m = statusMeta[status] || statusMeta.pending;

  return (
    <Tooltip title={m.label} arrow>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: m.color,
          }}
        />
      </Box>
    </Tooltip>
  );
}

function TeamMark({ status, theme }) {
  const done = status === "approved";
  return (
    <Box>
      {status === null ? (
        "-"
      ) : done ? (
        <CheckCircleIcon
          sx={{ fontSize: 18, color: theme.palette.brain.green.text3 }}
        />
      ) : (
        <CancelIcon
          sx={{ fontSize: 18, color: theme.palette.brain.red.text }}
        />
      )}
    </Box>
  );
}

function SearchField({ value, onChange, placeholder, theme }) {
  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        width: { xs: "100%", sm: 240 },
        "& .MuiOutlinedInput-root": {
          fontSize: "0.75rem",
          color: theme.palette.brain.table.row.text,
          backgroundColor: theme.palette.brain.table.tableBackground,
          "& fieldset": {
            borderColor: theme.palette.brain.mis.grey.border1,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.secondary.main2,
          },
          "&.Mui-focused fieldset": {
            border: `1px solid ${theme.palette.secondary.main2}`,
          },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 17, color: "text.secondary" }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onChange("")}
              sx={{ p: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: color }} />
      <Typography
        variant="caption"
        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function AgeCell({ age, theme }) {
  const critical = age > 3;
  return (
    <Tooltip title={critical ? "Aging beyond 5 days" : "Within SLA"} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          minWidth: 34,
          textAlign: "center",
          px: 0.75,
          py: 0.15,
          borderRadius: 1,
          fontSize: "0.72rem",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          color: critical
            ? theme.palette.brain.red.text
            : theme.palette.brain.table.row.text,
        }}
      >
        {age}d
      </Box>
    </Tooltip>
  );
}

const kpiStyle = {
  "Total UPC": {
    icon: Inventory2OutlinedIcon,
    color: "hsl(243, 75%, 59%)",
  },
  "UPC In Review (WIP)": {
    icon: RateReviewOutlinedIcon,
    color: "hsl(199, 89%, 48%)",
  },
  "Approved · Pending SSOT": {
    icon: VerifiedUserOutlinedIcon,
    color: "hsl(142, 71%, 40%)",
  },
  "UPCs in SSOT": {
    icon: StorageOutlinedIcon,
    color: "hsl(222, 20%, 40%)",
  },
  "Active Submissions": {
    icon: AssignmentTurnedInOutlinedIcon,
    color: "hsl(38, 92%, 45%)",
  },
  "Critical Aging": {
    icon: TimerOutlinedIcon,
    color: "hsl(0, 84%, 55%)",
    critical: true,
  },
};

const AgeDistributionChart = ({ data, theme, age_groups }) => {
  const chartData = useMemo(() => {
    const buckets = age_groups.map((group) => ({
      name: group.label,
      count: 0,
      color: group.color,
    }));

    data.forEach((item) => {
      for (let i = 0; i < age_groups.length; i++) {
        const group = age_groups[i];
        if (item.age >= group.min && item.age < group.max) {
          buckets[i].count += 1;
          break;
        }
      }
    });

    return buckets;
  }, [data]);

  return (
    <Box
      sx={{
        width: "100%",
        height: 230,
        padding: "8px",
        "& *": {
          outline: "none !important",
        },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            tickMargin={5}
            tick={{
              fontSize: 12,
              fill: theme.palette.brain.table.row.text,
            }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: theme.palette.brain.table.row.text }}
          />
          <RTooltip
            cursor={{
              fill: theme.palette.primary.searchBackgroundChat,
            }}
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: `1px solid ${theme.palette.brain.grey.border}`,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              background: theme.palette.primary.searchBackground,
            }}
            labelStyle={{
              color: theme.palette.brain.table.row.text,
            }}
            itemStyle={{
              color: theme.palette.brain.table.header.text,
            }}
            formatter={(value) => [value, "Total Count"]}
          />

          <Bar dataKey="count" radius={[4, 4, 0, 0]} activeBar={false}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const UpcAgeDistributionChart = ({ data, theme, age_groups }) => {
  const chartData = useMemo(() => {
    const buckets = age_groups.map((group) => ({
      name: group.label,
      upcCount: 0,
      color: group.color,
    }));

    data.forEach((item) => {
      for (let i = 0; i < age_groups.length; i++) {
        const group = age_groups[i];
        if (item.age >= group.min && item.age < group.max) {
          buckets[i].upcCount += 1;
          break;
        }
      }
    });

    return buckets;
  }, [data]);

  return (
    <Box
      sx={{
        width: "100%",
        height: 200,
        padding: "20px",
        "& *": {
          outline: "none !important",
        },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            tickMargin={5}
            tick={{
              fontSize: 12,
              fill: theme.palette.brain.table.row.text,
            }}
          />

          <YAxis
            allowDecimals={false}
            tick={{
              fontSize: 12,
              fill: theme.palette.brain.table.row.text,
            }}
          />

          <RTooltip
            cursor={{
              fill: theme.palette.primary.searchBackgroundChat,
            }}
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: `1px solid ${theme.palette.brain.grey.border}`,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              background: theme.palette.primary.searchBackground,
            }}
            labelStyle={{
              color: theme.palette.brain.table.row.text,
            }}
            itemStyle={{
              color: theme.palette.brain.table.header.text,
            }}
            formatter={(value) => [value, "Total UPCs"]}
          />

          <Bar
            dataKey="upcCount"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            activeBar={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const SubmissionDialog = ({
  batch,
  open,
  onClose,
  theme,
  classes,
  changeAssigendUser = () => {},
  dispatch,
  is_brain_admin = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [reviewer, setReviewer] = useState("");
  const [approver, setApprover] = useState("");
  const [isReAssigning, setIsReAssigning] = useState(false);
  useEffect(() => {
    if (batch.status === "success" && Object.keys(batch?.data).length !== 0) {
      setEditing(false);
      setReviewer(batch?.data?.submittedBy || "");
      setApprover(batch?.data?.assignedTo || "");
    }
  }, [batch]);

  const is_save_disabled = useMemo(() => {
    if (
      reviewer === batch.data.submittedBy &&
      approver === batch.data.assignedTo
    ) {
      return true;
    } else {
      return false;
    }
  }, [reviewer, approver, batch]);

  const handleReassign = async () => {
    const paylaod = {};
    let new_reviewer_name = "";
    let new_approver_name = "";
    if (reviewer !== batch.data.submittedBy) {
      const filtered_data = batch.data.reviewer.find(
        (item) => item.name === reviewer
      );
      new_reviewer_name = filtered_data.name;
      paylaod["reviewer"] = filtered_data.email;
    }
    if (approver !== batch.data.assignedTo) {
      const filtered_data = batch.data.approver.find(
        (item) => item.name === approver
      );

      new_approver_name = filtered_data.name;
      paylaod["approver"] = filtered_data.email;
    }
    if (Object.keys(paylaod).length === 0) {
      return;
    }

    try {
      setIsReAssigning(true);
      const response = await ReAssignUser(batch.data.submission_id, paylaod);
      if (response.data.status.statusCode === "nam-200") {
        changeAssigendUser(
          batch.data.submission_id,
          new_reviewer_name || batch?.data?.submittedBy,
          new_approver_name || batch?.data?.assignedTo
        );
        dispatch(
          updateAlertInfo({
            message: "User re-assigned successfully!",
            open: true,
            severity: "success",
          })
        );
      } else {
        setReviewer(batch?.data?.submittedBy);
        setApprover(batch?.data?.assignedTo);
        dispatch(
          updateAlertInfo({
            message: "Failed to re-assign user, please try again later",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (e) {
      setReviewer(batch?.data?.submittedBy);
      setApprover(batch?.data?.assignedTo);
      console.error("Error", e);
      dispatch(
        updateAlertInfo({
          message: "Internal Server Error!",
          open: true,
          severity: "error",
        })
      );
    }
    setIsReAssigning(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 10006 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          background: theme.palette.brain.table.tableBackground,
        },
      }}
    >
      {["initial", "loading"].includes(batch.status) ? (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              background: theme.palette.brain.table.header.background,
            }}
          >
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"40%"}
              height={35}
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: theme.palette.brain.table.row.text }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, mt: 2 }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={"250px"}
            />
          </DialogContent>
        </>
      ) : batch.status === "error" ? (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              mb: 1,
              background: theme.palette.brain.table.header.background,
            }}
          >
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: theme.palette.brain.table.row.text }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 2 }}>
            <Error
              message={"Failed to load data, Please try again later!"}
              fontSize={theme.size[16]}
              margin={theme.margin[1]}
              imageHeight={"30vh"}
              errHeight={"70vh"}
            />
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              background: theme.palette.brain.table.header.background,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{ color: theme.palette.secondary.main2 }}
                >
                  Submission #{batch?.data?.submission_id}
                </Typography>
                <StatusChip status={batch?.data?.submission_status} />
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.75 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Team: {batch?.data?.submission_team}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Age: {batch?.data?.submission_age} day
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={onClose} size="small">
                <CloseIcon sx={{ color: theme.palette.brain.table.row.text }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 2 }}>
            {/* Assignment */}
            <Box
              sx={{
                pb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: theme.palette.brain.table.header.text,
                  }}
                >
                  Assignment
                </Typography>

                {!editing ? (
                  <>
                    {is_brain_admin && (
                      <IconButton
                        size="small"
                        title="Re-assign Reviewer or Approver"
                      >
                        <Edit
                          size={14}
                          color={theme.palette.secondary.main2}
                          sx={{
                            color: theme.palette.secondary.main2,
                            fontSize: 16,
                          }}
                          onClick={() => setEditing(true)}
                        />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "flex", gap: 0 }}>
                    <IconButton disabled={false} size="small" title="Cancel">
                      <UndoIcon
                        sx={{
                          color: theme.palette.secondary.main2,
                          fontSize: 18,
                          padding: 0,
                        }}
                        onClick={() => {
                          setEditing(false);
                          setReviewer(batch?.data?.submittedBy);
                          setApprover(batch?.data?.assignedTo || "");
                        }}
                      />
                    </IconButton>

                    <IconButton
                      disabled={is_save_disabled}
                      size="small"
                      title="Save"
                    >
                      <SaveIcon
                        sx={{
                          color: is_save_disabled
                            ? theme.palette.brain.grey.text
                            : theme.palette.secondary.main2,
                          fontSize: 18,
                          padding: 0,
                        }}
                        onClick={() => handleReassign()}
                      />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {!editing ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <SummaryItem
                    label="Submitted by (Reviewer)"
                    value={formattedName(reviewer) || "—"}
                    theme={theme}
                  />
                  <SummaryItem
                    label="Assigned to (Approver)"
                    value={formattedName(approver) || "—"}
                    theme={theme}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: theme.palette.brain.table.header.text,
                      }}
                    >
                      Reviewer
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        displayEmpty
                        value={reviewer}
                        onChange={(e) => setReviewer(e.target.value)}
                        MenuProps={{
                          sx: {
                            zIndex: 100010,
                          },
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          backgroundColor:
                            theme.palette.brain.table.tableBackground,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.brain.mis.grey.border1,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.secondary.main2,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: `1px solid ${theme.palette.secondary.main2}`,
                          },
                        }}
                      >
                        {batch.data.reviewer.map((p) => (
                          <MenuItem
                            key={p.name}
                            value={p.name}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: theme.palette.brain.table.header.text,
                      }}
                    >
                      Approver
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        displayEmpty
                        value={approver}
                        onChange={(e) => setApprover(e.target.value)}
                        MenuProps={{
                          sx: {
                            zIndex: 100010,
                          },
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          backgroundColor:
                            theme.palette.brain.table.tableBackground,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.brain.mis.grey.border1,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.secondary.main2,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: `1px solid ${theme.palette.secondary.main2}`,
                          },
                        }}
                      >
                        {batch?.data?.approver.map((p) => (
                          <MenuItem
                            key={p.name}
                            value={p.name}
                            sx={{ fontSize: "0.85rem" }}
                          >
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}
            </Box>

            <TableContainer
              sx={{
                maxHeight: 350,
                marginTop: "16px",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                backgroundColor: theme.palette.brain.table.tableBackground,
                borderRadius: 2,
                "&::-webkit-scrollbar": { height: 6, width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.brain.mis.grey.background1,
                  borderRadius: 3,
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>UPC12</TableCell>
                    {batch.data.brain_teams.map((team, index) => (
                      <TableCell key={index} className={classes.tableHeader}>
                        {team}
                      </TableCell>
                    ))}
                    <TableCell className={classes.tableHeader}>Age</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.data.all_upc.map((r, idx) => {
                    return (
                      <TableRow
                        key={r.upc12}
                        hover
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            idx % 2 === 0
                              ? "transparent"
                              : theme.palette.brain.table.row.background,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.brain.table.row.hoverBackground,
                          },
                          transition: "background-color 0.12s ease",
                        }}
                      >
                        <TableCell
                          className={classes.baseCell}
                          sx={{
                            color: theme.palette.brain.table.row.text,
                          }}
                        >
                          {r.upc12}
                        </TableCell>
                        {r.teams.map((cur_team, index) => (
                          <TableCell
                            key={index}
                            className={classes.baseCell}
                            sx={{
                              color: theme.palette.brain.table.row.text,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <TeamMark
                                status={cur_team.status}
                                theme={theme}
                              />
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell
                          className={classes.baseCell}
                          sx={{
                            color: theme.palette.brain.table.row.text,
                          }}
                        >
                          <AgeCell age={r.age} theme={theme} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {batch.data.all_upc.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{
                          py: 3,
                          color: "text.secondary",
                          fontSize: "0.8rem",
                          color: theme.palette.brain.table.row.text,
                        }}
                      >
                        No UPCs match that search
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>

          {isReAssigning && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10001,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                bgcolor: theme.palette.brain.mis.grey.disabledBackground,
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress
                size={24}
                sx={{ color: theme.palette.secondary.main2 }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.brain.table.header.text,
                }}
              >
                Saving changes...
              </Typography>
            </Box>
          )}
        </>
      )}
    </Dialog>
  );
};
export default function Dashboard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [upcView, setUpcView] = useState("graph");
  const [upcQuery, setUpcQuery] = useState("");
  const [subView, setSubView] = useState("graph");
  const [subQuery, setSubQuery] = useState("");
  const dashboardData = useSelector((state) => state.brain.dashboardData);
  const is_brain_admin = useSelector(
    (state) => state.auth.metadata.brain.role === "admin"
  );
  const classes = brainStyles(theme);
  const { status, errorMessage, apiData, callApi } = useApi(getBrainDashboard);
  const [selectedSubmission, setSelectedSubmission] = useState({
    open: false,
    data: {},
    status: "initial",
  });
  const filteredUpcs = useMemo(
    () =>
      dashboardData.active_upc.filter((r) => r.upc12.includes(upcQuery.trim())),
    [dashboardData, upcQuery]
  );

  const filteredSubs = useMemo(
    () =>
      dashboardData.active_submission.filter((r) =>
        String(r.id).includes(subQuery.trim())
      ),
    [dashboardData, subQuery]
  );

  const AGE_GROUPS = useMemo(
    () => [
      {
        label: "< 1 day",
        min: 0,
        max: 1,
        color: theme.palette.brain.ageColor.l0,
      },
      {
        label: "1 - 2 days",
        min: 1,
        max: 2,
        color: theme.palette.brain.ageColor.l1,
      },
      {
        label: "2 - 3 days",
        min: 2,
        max: 3,
        color: theme.palette.brain.ageColor.l2,
      },
      {
        label: "3 - 4 days",
        min: 3,
        max: 4,
        color: theme.palette.brain.ageColor.l3,
      },
      {
        label: "4 - 5 days",
        min: 4,
        max: 5,
        color: theme.palette.brain.ageColor.l4,
      },
      {
        label: "≥ 5 days",
        min: 5,
        max: Infinity,
        color: theme.palette.brain.ageColor.l5,
      },
    ],
    []
  );

  const statusMeta = {
    initial: {
      label: "Not started",
      color: theme.palette.brain.grey.text,
      Icon: RemoveCircleOutlineIcon,
    },
    pending: {
      label: "Pending review",
      color: theme.palette.brain.yellow.text,
      Icon: ScheduleIcon,
    },
    approved: {
      label: "Approved",
      color: theme.palette.brain.green.text3,
      Icon: CheckCircleIcon,
    },
    rejected: {
      label: "Rejected",
      color: theme.palette.brain.red.text,
      Icon: CancelIcon,
    },
    returned_for_review: {
      label: "Returned for review",
      color: theme.palette.brain.red.text,
      Icon: CancelIcon,
    },
    resubmitted: {
      label: "Re-submitted",
      color: theme.palette.brain.blue.text,
      Icon: ReplayIcon,
    },
  };

  const loadData = async () => {
    const response = await callApi();

    dispatch(
      updateDashboardData({
        kpi: response.data.kpi,
        team_performance_chart: response.data.team_performance_chart,
        active_upc: response.data.active_upc,
        active_submission: response.data.active_submission,
        review_teams: response.data.brain_teams,
      })
    );
  };

  const handleOpenSubmission = async (cur_sub) => {
    try {
      setSelectedSubmission((prev) => ({
        ...prev,
        open: true,
        status: "loading",
      }));
      let is_api_success = true;
      let user_reassign_resp = null;
      const submission_resp = await getSubmissionDashboardData(cur_sub.id);
      is_api_success =
        is_api_success && submission_resp.data.status.statusCode === "nam-200";
      if (is_brain_admin) {
        user_reassign_resp = await getUserReAssignList(cur_sub.id);
        is_api_success =
          is_api_success &&
          user_reassign_resp.data.status.statusCode === "nam-200";
      } else {
      }

      if (is_api_success) {
        setSelectedSubmission((prev) => ({
          ...prev,
          data: {
            ...submission_resp.data.data,
            reviewer: is_brain_admin
              ? user_reassign_resp.data.data.reviewer
              : [],
            approver: is_brain_admin
              ? user_reassign_resp.data.data.approver
              : [],
            submission_id: cur_sub.id,
            submission_team: cur_sub.team,
            submission_status: cur_sub.status,
            submittedBy: cur_sub.submittedBy,
            assignedTo: cur_sub.assignedTo,
            submission_age: cur_sub.age,
            submittedAt: "2026-07-03 07:43:08.095",
          },
          status: "success",
        }));
      } else {
        setSelectedSubmission((prev) => ({
          ...prev,
          data: {},
          status: "error",
        }));
      }
    } catch (e) {
      setSelectedSubmission((prev) => ({
        ...prev,
        data: {},
        status: "error",
      }));
      console.error("Error", e);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  console.log("got-data", status);
  return (
    <>
      {status === APP_CONSTANTS.LOADING || status === APP_CONSTANTS.INITIAL ? (
        <Loader />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 3,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              gap: 2,
              flexDirection: "column",
            }}
          >
            {/* KPI STRIP */}
            <Card
              sx={{ overflow: "hidden", flexShrink: 0, margin: "3px 24px" }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(6, 1fr)",
                  },
                }}
              >
                {dashboardData.kpi.map((k, i) => {
                  const Icon = kpiStyle[k.title].icon;
                  const isCritical = kpiStyle[k.title]?.critical || false;
                  const color = kpiStyle[k.title].color;
                  return (
                    <Tooltip key={k.title} title={k.hint} arrow>
                      <Box
                        sx={{
                          p: 1.75,
                          borderRight:
                            i === dashboardData.kpi.length - 1
                              ? "none"
                              : `1px solid ${theme.palette.brain.grey.border}`,

                          borderBottom: `1px solid ${theme.palette.brain.grey.border}`,
                          bgcolor: isCritical
                            ? theme.palette.brain.rejected.background
                            : theme.palette.primary.searchBackground,
                          "&:hover": {
                            bgcolor: isCritical
                              ? theme.palette.brain.red.background
                              : theme.palette.primary.searchBackgroundChat,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mb: 0.75,
                          }}
                        >
                          <Icon sx={{ fontSize: 15, color: color }} />
                          <Typography
                            sx={{
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              letterSpacing: "0.03em",
                              color: isCritical
                                ? "error.main"
                                : theme.palette.brain.table.header.text,
                              lineHeight: 1.2,
                            }}
                          >
                            {k.title}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            lineHeight: 1.1,
                            color: isCritical
                              ? "error.main"
                              : theme.palette.brain.table.row.text,
                          }}
                        >
                          {k.value}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Card>

            {/* Team CHART + Submission Tracker */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "360px 1fr" },
                gap: 2,
                alignItems: "stretch",
                margin: "0 24px",
              }}
            >
              {/* Team Performance Bar Chart */}
              <Card>
                <CardContent
                  sx={{
                    background: theme.palette.primary.searchBackground,
                    p: 2,
                    "&:last-child": { pb: 2 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "0.95rem",
                      color: theme.palette.brain.table.row.text,
                    }}
                  >
                    Active submissions by team
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 0.5, mb: 1 }}>
                    <LegendDot
                      color={theme.palette.brain.grey.text}
                      label="Fresh < 5d"
                    />
                    <LegendDot
                      color={theme.palette.brain.red.text}
                      label="Critical ≥ 5d"
                    />
                  </Box>
                  <Box
                    sx={{
                      height: 200,
                      "& *": {
                        outline: "none !important",
                      },
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dashboardData.team_performance_chart}
                        margin={{ top: 4, right: 8, left: -22, bottom: 0 }}
                        barSize={30}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={theme.palette.brain.grey.text}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="team"
                          tick={{
                            fontSize: 12,
                            fill: theme.palette.brain.table.row.text,
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{
                            fontSize: 12,
                            fill: theme.palette.brain.table.row.text,
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <RTooltip
                          cursor={{
                            fill: theme.palette.primary.searchBackgroundChat,
                          }}
                          contentStyle={{
                            fontSize: 12,
                            borderRadius: 8,
                            border: `1px solid ${theme.palette.brain.grey.border}`,
                            background: theme.palette.primary.searchBackground,
                          }}
                          labelStyle={{
                            color: theme.palette.brain.table.row.text,
                          }}
                        />
                        <Bar
                          dataKey="fresh"
                          name="Fresh"
                          stackId="a"
                          fill={"#78909C"}
                          radius={[0, 0, 3, 3]}
                          activeBar={false}
                        />
                        <Bar
                          dataKey="critical"
                          name="Critical"
                          stackId="a"
                          fill={theme.palette.brain.ageColor.l5}
                          radius={[3, 3, 0, 0]}
                          activeBar={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>

              {/* Submission level Trakcing */}
              <Card sx={{ background: theme.palette.primary.searchBackground }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: theme.palette.brain.table.row.text,
                      fontSize: "0.95rem",
                    }}
                  >
                    Active submissions
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {subView === "table" && (
                      <SearchField
                        value={subQuery}
                        onChange={setSubQuery}
                        placeholder="Search submission ID"
                        theme={theme}
                      />
                    )}
                    <ViewToggle
                      value={subView}
                      onChange={setSubView}
                      theme={theme}
                    />
                  </Box>
                </Box>
                <Divider
                  sx={{
                    borderColor: theme.palette.brain.grey.border,
                  }}
                />
                {subView === "graph" ? (
                  <>
                    <AgeDistributionChart
                      data={dashboardData.active_submission}
                      theme={theme}
                      age_groups={AGE_GROUPS}
                    />
                  </>
                ) : (
                  <TableContainer sx={{ maxHeight: 220 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHeader}>
                            ID
                          </TableCell>
                          <TableCell className={classes.tableHeader}>
                            Team
                          </TableCell>
                          <TableCell className={classes.tableHeader}>
                            Submitted by
                          </TableCell>
                          <TableCell className={classes.tableHeader}>
                            Assigned to
                          </TableCell>
                          <TableCell
                            className={classes.tableHeader}
                            align="right"
                          >
                            Age
                          </TableCell>
                          <TableCell
                            className={classes.tableHeader}
                            align="center"
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSubs.map((r, idx) => (
                          <TableRow
                            key={r.id}
                            hover
                            sx={{
                              cursor: "pointer",
                              backgroundColor:
                                idx % 2 === 0
                                  ? "transparent"
                                  : theme.palette.brain.table.row.background,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.brain.table.row.hoverBackground,
                              },
                              transition: "background-color 0.12s ease",
                            }}
                          >
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <StatusDot
                                  status={r.status}
                                  statusMeta={statusMeta}
                                />
                                <span> #{r.id}</span>
                              </Box>
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              {r.team}
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              {formattedName(r.submittedBy)}
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color:
                                  r.assignedTo === "Unassigned"
                                    ? "text.secondary"
                                    : theme.palette.brain.table.row.text,
                                fontStyle:
                                  r.assignedTo === "Unassigned"
                                    ? "italic"
                                    : "normal",
                              }}
                            >
                              {formattedName(r.assignedTo)}
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              align="right"
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              <AgeCell age={r.age} theme={theme} />
                            </TableCell>
                            <TableCell
                              className={classes.baseCell}
                              align="center"
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              <Tooltip title="Open">
                                <IconButton size="small">
                                  <Eye
                                    size={14}
                                    color={theme.palette.secondary.main2}
                                    onClick={() => {
                                      handleOpenSubmission(r);
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredSubs.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{
                                py: 3,
                                color: "text.secondary",
                                fontSize: "0.8rem",
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              No submissions match that search
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </Box>

            {/* UPC level tracking */}
            <Card
              sx={{
                display: "flex",
                flexDirection: "column",
                background: theme.palette.primary.searchBackground,
                flexShrink: 0,
                margin: "0 24px 16px 24px",
                // flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      color: theme.palette.brain.table.row.text,
                      fontSize: "0.95rem",
                    }}
                  >
                    UPCs needing attention
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.72rem" }}
                  >
                    Teams marked{" "}
                    <CheckCircleIcon
                      sx={{
                        fontSize: 12,
                        verticalAlign: -1.5,
                        color: theme.palette.brain.green.text3,
                      }}
                    />{" "}
                    are done ·{" "}
                    <CancelIcon
                      sx={{
                        fontSize: 12,
                        verticalAlign: -1.5,
                        color: theme.palette.brain.error.main,
                      }}
                    />{" "}
                    are bottlenecks
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {upcView === "table" && (
                    <SearchField
                      value={upcQuery}
                      onChange={setUpcQuery}
                      placeholder="Search UPC12"
                      theme={theme}
                    />
                  )}
                  <ViewToggle
                    theme={theme}
                    value={upcView}
                    onChange={setUpcView}
                  />
                </Box>
              </Box>
              <Divider
                sx={{
                  borderColor: theme.palette.brain.grey.border,
                }}
              />

              {upcView === "graph" ? (
                <>
                  <UpcAgeDistributionChart
                    data={dashboardData.active_upc}
                    theme={theme}
                    age_groups={AGE_GROUPS}
                  />
                </>
              ) : (
                <TableContainer sx={{ maxHeight: 268 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeader}>
                          UPC12
                        </TableCell>
                        {dashboardData.review_teams.map((team, index) => (
                          <TableCell
                            key={index}
                            className={classes.tableHeader}
                          >
                            {team}
                          </TableCell>
                        ))}
                        <TableCell className={classes.tableHeader}>
                          Age
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUpcs.map((r, idx) => {
                        return (
                          <TableRow
                            key={r.upc12}
                            hover
                            sx={{
                              cursor: "pointer",
                              backgroundColor:
                                idx % 2 === 0
                                  ? "transparent"
                                  : theme.palette.brain.table.row.background,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.brain.table.row.hoverBackground,
                              },
                              transition: "background-color 0.12s ease",
                            }}
                          >
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              {r.upc12}
                            </TableCell>
                            {r.teams.map((cur_team, index) => (
                              <TableCell
                                key={index}
                                className={classes.baseCell}
                                sx={{
                                  color: theme.palette.brain.table.row.text,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <TeamMark
                                    status={cur_team.status}
                                    theme={theme}
                                  />
                                </Box>
                              </TableCell>
                            ))}
                            <TableCell
                              className={classes.baseCell}
                              sx={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              <AgeCell age={r.age} theme={theme} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredUpcs.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              py: 3,
                              color: "text.secondary",
                              fontSize: "0.8rem",
                              color: theme.palette.brain.table.row.text,
                            }}
                          >
                            No UPCs match that search
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
          {selectedSubmission.open && (
            <SubmissionDialog
              batch={selectedSubmission}
              open={selectedSubmission.open}
              onClose={() => {
                setSelectedSubmission((prev) => ({
                  ...prev,
                  open: false,
                  data: {},
                  status: "initial",
                }));
              }}
              changeAssigendUser={(submission_id, reviewer, approver) => {
                setSelectedSubmission((prev) => ({
                  ...prev,
                  data: {
                    ...prev.data,
                    submittedBy: reviewer,
                    assignedTo: approver,
                  },
                }));

                dispatch(
                  updateDashboardData({
                    active_submission: dashboardData.active_submission.map(
                      (item) => {
                        if (item.id === submission_id) {
                          return {
                            ...item,
                            submittedBy: reviewer,
                            assignedTo: approver,
                          };
                        } else {
                          return item;
                        }
                      }
                    ),
                  })
                );
              }}
              theme={theme}
              classes={classes}
              dispatch={dispatch}
              is_brain_admin={is_brain_admin}
            />
          )}
        </Box>
      )}
    </>
  );
}

function SummaryItem({ label, value, theme }) {
  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.05em",
          color: theme.palette.brain.table.header.text,
        }}
      >
        {label}:
      </Typography>
      <Box
        sx={{
          // mt: 0.35,
          fontSize: "0.75rem",
          fontWeight: 600,
          color:
            value === "Unassigned"
              ? "text.secondary"
              : theme.palette.brain.yellow.text,
          fontStyle: value === "Unassigned" ? "italic" : "normal",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}
